import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import success from "../../assets/SuccessAct.png";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import HomeIcon from "@mui/icons-material/Home";
import { useLocation, useNavigate } from "react-router-dom";
import Paymentfailure from "../horecaWidget/Paymentfailure";
import PaymentPending from "../horecaWidget/PaymentPending";
import { ModuleTypes } from "../../constants/moduleTypes";
import {
  FetchWidgetConfigDetails,
  getReservationPdf,
} from "../../api/HorecaWidget.service";
import { useDispatch, useSelector } from "react-redux";
import { setLanguageWords } from "../../store/slice/Addguest.slice";
import { saveAs } from "file-saver";
import { updateCss, updateWidgetConfig } from "../../store/slice/widgetSetting.slice";
import { naarOuder } from '../../lib/bericht';
import { fout } from '../../lib/logboek';

const PaymentSuccessActivity = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const key = urlParams.get("key");
  const secretKey = urlParams.get("secretkey");
  const widgetId = urlParams.get("widgetId");
  const lang = urlParams.get("lang");
  const mainModuleId = urlParams.get("mainmoduleid");
  const [resType, setResType] = useState("");

  const { languageWords } = useSelector((store) => store.addGuest);
  const { widgetConfig, css } = useSelector((store) => store?.widgetSettings);
  const Css = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Success and cancel"
  )?.fields;
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const deviceId = JSON.parse(localStorage.getItem("deviceId"));
  const isKiosk = !!(deviceId && deviceId !== "null");
  const [timer] = useState(15000);
  const [countdown, setCountdown] = useState(timer / 1000);
  const [pdfData, setPdfData] = useState(null);
  const [reservationNumber, setReservationNumber] = useState("");
  const [widgetLoading, setWidgetLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const fetchWidgetSettingsDetails = async (key, lang, id) => {
    try {
      setWidgetLoading(true);
      const response = await FetchWidgetConfigDetails(key, id, lang);
      const result = response?.data?.basicLanguages?.reduce((acc, item) => {
        const tag = item.tag.replace(/{|}/g, "").replace(/\s+/g, "");
        acc[tag] = item.widgetTranslateLanguages[0]?.name;
        return acc;
      }, {});
      dispatch(updateCss(response?.data?.settings?.cssGroup));
        dispatch(updateWidgetConfig(response?.data));
      dispatch(setLanguageWords(result));
      setWidgetLoading(false);
    } catch (error) {
      fout("Error fetching widget settings details:", error);
      setWidgetLoading(false);
    }
  };

  const fetchReservationPdf = async (key, secretKey, mainModuleId) => {
    try {
      const base64Data = await getReservationPdf(key, secretKey, mainModuleId);
      if (base64Data) {
        setPdfData(base64Data?.pdf);
        setReservationNumber(base64Data?.reservationNumber);
      }
    } catch (error) {
      fout("Error fetching PDF data:", error);
    }
  };

  const send = (frameHeight, frameWidth, top, right, bottom, left, floating, redirectUrl) => {
    naarOuder(
      {
        reservationIframeHeight: frameHeight,
        reservationIframeWidth: frameWidth,
        reservationIframePositionTop: top,
        reservationIframePositionRight: right,
        reservationIframePositionBottom: bottom,
        reservationIframePositionLeft: left,
        reservationIframePositionFloating: floating,
        redirectUrl: redirectUrl,
      });
  };

  const downloadPdf = () => {
    if (!pdfData) return;
    const sanitizedBase64String = pdfData.replace(/\s/g, "");
    if (!/^[A-Za-z0-9+/=]+$/.test(sanitizedBase64String)) return;
    try {
      const binaryData = atob(sanitizedBase64String);
      const byteArray = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        byteArray[i] = binaryData.charCodeAt(i);
      }
      saveAs(new Blob([byteArray], { type: "application/pdf" }), "reservation.pdf");
    } catch (error) {
      fout("Error decoding base64 string:", error);
    }
  };

  useEffect(() => {
    if (key && secretKey && mainModuleId) {
      fetchReservationPdf(key, secretKey, mainModuleId);
    }
  }, [key, secretKey, mainModuleId]);

  useEffect(() => {
    if (key && lang && widgetId) {
      fetchWidgetSettingsDetails(key, lang, widgetId);
    }
  }, []);

  useEffect(() => {
    const moduleName = Object.keys(ModuleTypes).find(
      (k) => ModuleTypes[k] === mainModuleId
    );
    if (moduleName) {
      const resolved = moduleName.toLowerCase();
      setResType(resolved);
    }
  }, [mainModuleId]);

  /**
   * Central navigation handler.
   * - Kiosk: navigates directly within the kiosk window using the stored widget URL.
   * - Non-kiosk: navigates the *parent* window so the widget loads in the same
   *   context as it was originally opened (same-origin direct set; cross-origin
   *   falls back to postMessage so the parent page handles the redirect).
   */
  const navigateHome = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (isKiosk) {
      const storedUrl = localStorage.getItem("kioskReturnUrl");
      window.location.href =
        storedUrl ||
        `/widget?widgetsetupid=${widgetId}&key=${key}&resType=${resType}&deviceId=${encodeURIComponent(deviceId)}`;
    } else {
      // Prefer the widget-configured link; fall back to the stored parent URL
      const targetUrl =
        widgetConfig?.settings?.widgetLink || localStorage.getItem("parentUrl");
      if (window.parent && window.parent !== window) {
        try {
          // Same-origin: set directly so browser history is preserved in parent
          window.parent.location.href = targetUrl;
        } catch {
          // Cross-origin: delegate to parent via postMessage
          send("100vh", "100%", "", "0px", "0px", "", "relative", targetUrl);
        }
      } else if (targetUrl) {
        window.location.href = targetUrl;
      }
    }
  };

  const resetIdleTimer = () => {
    setCountdown((prev) => (prev === timer / 1000 ? prev : timer / 1000));
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    let count = timer / 1000;
    setCountdown(count);

    intervalRef.current = setInterval(() => {
      count -= 1;
      setCountdown(count);
      if (count <= 0) clearInterval(intervalRef.current);
    }, 1000);

    timeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current);
      navigateHome();
    }, timer);
  };

  useEffect(() => {
    if (deviceId && deviceId !== "null") {
      resetIdleTimer();
      const clickHandler = () => navigateHome();
      const dblClickHandler = () => navigateHome();
      const contextMenuHandler = (e) => { e.preventDefault(); navigateHome(); };

      window.addEventListener("click", clickHandler);
      window.addEventListener("dblclick", dblClickHandler);
      window.addEventListener("contextmenu", contextMenuHandler);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        window.removeEventListener("click", clickHandler);
        window.removeEventListener("dblclick", dblClickHandler);
        window.removeEventListener("contextmenu", contextMenuHandler);
      };
    }
  }, []);

  if (widgetLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5eb",
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          gap: 2,
        }}
      >
        {/* Image placeholder */}
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            borderRadius: 2,
            width: { xs: 140, sm: 170, md: 200 },
            height: { xs: 110, sm: 135, md: 160 },
            flexShrink: 0,
          }}
        />
        {/* Heading */}
        <Skeleton variant="rounded" animation="wave" width="55%" height={34} />
        {/* Subtext lines */}
        <Skeleton variant="text" animation="wave" width="45%" height={20} />
        <Skeleton variant="text" animation="wave" width="38%" height={20} />
        {/* Reservation number */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Skeleton variant="text" animation="wave" width={130} height={20} />
          <Skeleton variant="rounded" animation="wave" width={80} height={44} />
        </Box>
        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
          <Skeleton variant="rounded" animation="wave" width={140} height={42} />
          <Skeleton variant="rounded" animation="wave" width={140} height={42} />
        </Box>
      </Box>
    );
  }

  if (!key || !secretKey) return <Paymentfailure Css={Css} />;

  if (key && secretKey === "pending") {
    if (!isKiosk) send("100vh", "100%", "", "0px", "0px", "", "relative");
    return <PaymentPending Css={Css} />;
  }

  if (key && secretKey !== "cancel" && resType !== "cancel") {
    if (!isKiosk) send("100vh", "100%", "", "0px", "0px", "", "relative");

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          textAlign: "center",
          backgroundColor: Css?.SucessbgClr || "#f5f5eb",
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          gap: 0,
        }}
      >
        {/* Success Image — tighter sizing */}
        <Box
          sx={{
            width: { xs: "140px", sm: "170px", md: "200px" },
            height: { xs: "110px", sm: "135px", md: "160px" },
            mb: { xs: 1.5, sm: 2 },
            flexShrink: 0,
          }}
        >
          <img
            src={success}
            alt="success"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>

        {/* Heading */}
        <Typography
          sx={{
            color: Css?.SucessHeadingClr || "#e8793a",
            fontSize: { xs: "18px", sm: "24px", md: "30px" },
            fontWeight: 700,
            lineHeight: 1.2,
            mb: 0.75,
          }}
        >
          {languageWords?.ReservationSuccessMessage}
        </Typography>

        {/* Subtext */}
        <Typography
          sx={{
            color: Css?.SuccessInfoClr || "#666",
            fontSize: { xs: "11px", sm: "12px", md: "13px" },
            mb: reservationNumber ? 1.5 : 2,
            maxWidth: "420px",
            lineHeight: 1.5,
          }}
        >
          {languageWords?.ReservationSuccessInfo}
        </Typography>

        {/* Reservation Number */}
        {reservationNumber && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: { xs: 0.25, sm: 1 },
              mb: isKiosk ? 1 : 2,
            }}
          >
            <Typography
              sx={{
                color: Css?.ReservationNumTxtClr || "#555",
                fontSize: { xs: "13px", sm: "14px" },
                fontWeight: 500,
              }}
            >
              {languageWords?.reservationNumberText}
            </Typography>
            <Typography
              sx={{
                color: Css?.ReservatioNumClr || "#e8793a",
                fontSize: { xs: "26px", sm: "32px", md: "36px" },
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              {reservationNumber}
            </Typography>
          </Box>
        )}

        {/* Countdown */}
        {isKiosk && (
          <Typography
            variant="body2"
            sx={{
              color: "#888",
              fontSize: { xs: "12px", sm: "13px" },
              mb: 2,
            }}
          >
            {languageWords?.return_home} {countdown} {languageWords?.seconds}
          </Typography>
        )}

        {/* Action Buttons — hidden in kiosk mode (countdown auto-redirects) */}
        {!isKiosk && (
          // ── Non-kiosk mode: Back to Home + PDF download ────────────────────
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              mt: 0.5,
              width: { xs: "100%", sm: "auto" },
              maxWidth: "360px",
            }}
          >
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={navigateHome}
              sx={{
                backgroundColor: Css?.SucessbackBtnBgClr || "#2d3748",
                color: Css?.SucessbackBtnIconClr || "#fff",
                fontSize: { xs: "11px", sm: "12px" },
                px: 2.5,
                py: 1,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                borderRadius: "6px",
                flex: { xs: 1, sm: "unset" },
                "&:hover": {
                  backgroundColor: Css?.SucessbackBtnBgClr
                    ? `${Css.SucessbackBtnBgClr}dd`
                    : "#1a202c",
                },
              }}
            >
              {languageWords?.BackToHome}
            </Button>

            {pdfData && (
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon sx={{ color: Css?.downloadPdfBtnIconTxtClr || "#e8793a" }} />}
                onClick={downloadPdf}
                sx={{
                  backgroundColor: Css?.downloadPdfBtnBGClr || "transparent",
                  color: Css?.downloadPdfBtnIconTxtClr || "#e8793a",
                  borderColor: Css?.downloadPdfBtnIconTxtClr || "#e8793a",
                  fontSize: { xs: "11px", sm: "12px" },
                  px: 2.5,
                  py: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderRadius: "6px",
                  flex: { xs: 1, sm: "unset" },
                  "&:hover": {
                    backgroundColor: `${Css?.downloadPdfBtnIconTxtClr || "#e8793a"}15`,
                    borderColor: Css?.downloadPdfBtnIconTxtClr || "#e8793a",
                  },
                }}
              >
                {languageWords?.DownloadPdf}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (!isKiosk) send("100vh", "100%", "", "0px", "0px", "", "relative");
  return <Paymentfailure />;
};

export default PaymentSuccessActivity;
